'use client';

import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { Waves, Dumbbell, Flame, Heart, ChevronDown, Gamepad2, Flower2, Sofa, Palette, Baby } from 'lucide-react';
import { RiBilliardsFill, RiBoxingFill } from 'react-icons/ri';
import { GiPingPongBat } from 'react-icons/gi';
import { PiBoxingGlove } from 'react-icons/pi';


type AmenityIcon = React.ComponentType<{ className?: string }>;

const amenities: { icon: AmenityIcon; title: string }[] = [
  { icon: Waves, title: 'Infinity Pool' },
  { icon: Flower2, title: 'Boho Garden' },
  { icon: Baby, title: 'Kids Area' },
  { icon: GiPingPongBat, title: 'Table Tennis' },
  { icon: Sofa, title: 'Lobby Lounge' },
  { icon: Flame, title: 'Sauna' },
  { icon: Heart, title: 'Yoga Studio' },
  { icon: Dumbbell, title: 'Gym' },
  { icon: Palette, title: 'Kids Art Studio' },
  { icon: RiBilliardsFill, title: 'Pool Table' },
  { icon: PiBoxingGlove, title: 'Boxing Studio' },
  { icon: Gamepad2, title: 'Gaming Room' },
];

export function AmenitiesSection() {
  const [showAll, setShowAll] = useState(false);

  return (
    <section
      id="amenities"
      className="relative py-24 md:py-36"
      style={{ backgroundImage: "url('/images/gallery/bg-2-new.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-white/25 z-0" />
      <div className="relative container mx-auto px-4 md:px-6 z-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-[1px] bg-[#a88555] w-[80px] mx-auto mb-8 origin-center"
          />
          <h2 className="text-[#a88555] tracking-[0.15em] font-light">EXCEPTIONAL AMENITIES</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-16 max-w-5xl mx-auto">
          {amenities.map((amenity, index) => {
            const Icon = amenity.icon;
            const isHidden = index >= 6 && !showAll;

            return (
              <AnimatePresence key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className={`text-center group ${isHidden ? 'hidden md:block' : ''}`}
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 inline-block"
                  >
                    <Icon className="w-10 h-10 md:w-12 md:h-12 text-[#a88555] group-hover:text-[#8a6b3f] transition-colors" />
                  </motion.div>
                  <h3 className="text-[#8a6b3f] text-sm md:text-base tracking-wider font-light">{amenity.title}</h3>
                </motion.div>
              </AnimatePresence>
            );
          })}
        </div>

        {/* Show More Button - Only on Mobile */}
        <motion.div
          className="flex justify-center mt-12 md:hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <motion.button
            onClick={() => setShowAll(!showAll)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 bg-[#c19964] text-white px-8 py-3 tracking-[0.2em] text-sm transition-all duration-300"
          >
            {showAll ? 'SHOW LESS' : 'SHOW MORE'}
            <motion.div
              animate={{ rotate: showAll ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}